import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./client";

export type CustomerRow = {
  id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  branch_name: string | null;
  branch_address: string | null;
  admin_user_id: string | null;
  admin_name: string | null;
  admin_email: string | null;
  admin_phone: string | null;
  total_students: number;
  total_instructors: number;
  total_vehicles: number;
  total_users: number;
};

export type CustomerRecordKind = "students" | "instructors" | "vehicles" | "users";

export type CustomerRecordItem = {
  id: string;
  title: string;
  subtitle: string | null;
  status: string | null;
  branch_name: string | null;
};

export type CustomerRecordsResponse = {
  tenant_id: string;
  tenant_name: string;
  kind: CustomerRecordKind;
  total: number;
  items: CustomerRecordItem[];
};

export type CustomerListResponse = {
  rows: CustomerRow[];
  total: number;
};

export type CreateCustomerInput = {
  tenantName: string;
  branchName: string;
  adminName: string;
  adminEmail: string;
  adminPassword: string;
  adminPhone?: string;
  branchAddress?: string;
};

export type CreateCustomerResult = {
  tenant_id: string;
  tenant_name: string;
  branch_id: string;
  branch_name: string;
  admin_user_id: string;
  admin_name: string;
  admin_email: string;
  login_hint: string;
};

export type UpdateCustomerInput = {
  tenantName: string;
  adminName?: string;
  adminEmail?: string;
  adminPhone?: string;
  branchAddress?: string;
  isActive: boolean;
};

export type UpdateCustomerResult = {
  tenant_id: string;
  tenant_name: string;
  is_active: boolean;
  admin_user_id: string | null;
  admin_name: string | null;
  admin_email: string | null;
  login_hint: string;
};

export type ResetAdminPasswordResult = {
  ok: true;
  admin_email: string;
  login_hint: string;
};

export async function listCustomers(): Promise<CustomerListResponse> {
  return api.get<CustomerListResponse>("/platform/customers");
}

export async function listCustomerRecords(
  tenantId: string,
  kind: CustomerRecordKind,
): Promise<CustomerRecordsResponse> {
  return api.get<CustomerRecordsResponse>(`/platform/customers/${tenantId}/records`, { kind });
}

export async function createCustomer(input: CreateCustomerInput): Promise<CreateCustomerResult> {
  return api.post<CreateCustomerResult>("/platform/customers", input);
}

export async function updateCustomer(
  tenantId: string,
  input: UpdateCustomerInput,
): Promise<UpdateCustomerResult> {
  return api.patch<UpdateCustomerResult>(`/platform/customers/${tenantId}`, input);
}

export async function resetCustomerAdminPassword(
  tenantId: string,
  newPassword: string,
): Promise<ResetAdminPasswordResult> {
  return api.post<ResetAdminPasswordResult>(`/platform/customers/${tenantId}/reset-admin-password`, {
    newPassword,
  });
}

export function useCustomerList() {
  const [data, setData] = useState<CustomerListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await listCustomers());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Musteriler yuklenemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, loading, error, reload };
}
